import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { fmtDT } from "../../../lib/constants";
import { toast } from "sonner";
import { Truck, ShieldCheck } from "@phosphor-icons/react";
import { Info, Panel } from "./_shared";

export function CityOpsSideRow({ side, s, isSuperAdmin, profile, users, reload }) {
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [ctrlPick, setCtrlPick] = useState("");
  const [ctrlBusy, setCtrlBusy] = useState(false);
  const [capPick, setCapPick] = useState("");
  const [capBusy, setCapBusy] = useState(false);
  const [ridPick, setRidPick] = useState("");
  const [ridBusy, setRidBusy] = useState(false);
  const [acctPick, setAcctPick] = useState("");
  const [acctBusy, setAcctBusy] = useState(false);
  const findUser = (uid) => users.find((u) => u.id === uid);
  const airport = side === "SOURCE" ? s.source_airport : s.destination_airport;
  const controllerId = side === "SOURCE" ? s.source_city_controller_id : s.destination_city_controller_id;
  const ctopId = side === "SOURCE" ? s.source_ctop_id : s.destination_ctop_id;
  const controller = findUser(controllerId);
  const assignedCtop = findUser(ctopId);
  // A Source Controller may assign Source CTOP only; Destination Controller -> Destination CTOP only.
  const isSideController = profile?.role === "CTRL" && controllerId === profile?.id;
  const canAssign = isSuperAdmin || isSideController;
  const eligible = users.filter(
    (u) => u.role === "CTOP" && (u.account_status || "ACTIVE") === "ACTIVE" && u.occupancy === "OCCUPIED" && u.airport_code === airport
  );
  // SUPER_ADMIN-only: assign/reassign the side's City Controller (CTRL @ that airport).
  const eligibleCtrl = users.filter(
    (u) => u.role === "CTRL" && (u.account_status || "ACTIVE") === "ACTIVE" && u.occupancy === "OCCUPIED" && u.airport_code === airport
  );
  const assignController = async () => {
    if (!ctrlPick) return;
    setCtrlBusy(true);
    try {
      await api.post(`/admin/shipments/${s.id}/city-controller/assign`, { side, controller_profile_id: ctrlPick });
      toast.success(`${side} Controller ${controller ? "reassigned" : "assigned"}`);
      setCtrlPick("");
      reload();
    } catch (e) { toast.error(e.displayMessage); }
    finally { setCtrlBusy(false); }
  };
  const assign = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      await api.post(`/admin/shipments/${s.id}/city-ctop/assign`, { side, ctop_profile_id: pick });
      toast.success(`${side} CTOP ${assignedCtop ? "reassigned" : "assigned"}`);
      setPick("");
      reload();
    } catch (e) { toast.error(e.displayMessage); }
    finally { setBusy(false); }
  };
  // M3 — Captain/Rider (assigned by SUPER_ADMIN or the matching side's CTOP).
  const capId = side === "SOURCE" ? s.source_captain_id : s.destination_captain_id;
  const ridId = side === "SOURCE" ? s.pickup_rider_id : null;
  const capLabel = side === "SOURCE" ? "Source Captain" : "Destination Captain";
  const ridLabel = "Pickup Rider";
  const assignedCap = findUser(capId);
  const assignedRid = findUser(ridId);
  const canAssignOps = isSuperAdmin || (profile?.role === "CTOP" && ctopId === profile?.id);
  // A9 — the matching side's assigned CTRL may REASSIGN an existing Captain (never initial; never Rider).
  const sideCtrlId = side === "SOURCE" ? s.source_city_controller_id : s.destination_city_controller_id;
  const canAssignCaptain = canAssignOps || (profile?.role === "CTRL" && sideCtrlId === profile?.id && !!capId);
  const eligibleCap = users.filter((u) => u.role === "CAP" && (u.account_status || "ACTIVE") === "ACTIVE" && u.occupancy === "OCCUPIED" && u.airport_code === airport);
  const eligibleRid = users.filter((u) => u.role === "RID" && (u.account_status || "ACTIVE") === "ACTIVE" && u.occupancy === "OCCUPIED" && u.airport_code === airport);
  const assignCaptain = async () => {
    if (!capPick) return; setCapBusy(true);
    try { await api.post(`/admin/shipments/${s.id}/city-captain/assign`, { side, captain_profile_id: capPick }); toast.success(`${capLabel} ${assignedCap ? "reassigned" : "assigned"}`); setCapPick(""); reload(); }
    catch (e) { toast.error(e.displayMessage); } finally { setCapBusy(false); }
  };
  const assignRider = async () => {
    if (!ridPick) return; setRidBusy(true);
    try { await api.post(`/admin/shipments/${s.id}/city-rider/assign`, { side, rider_profile_id: ridPick }); toast.success(`${ridLabel} ${assignedRid ? "reassigned" : "assigned"}`); setRidPick(""); reload(); }
    catch (e) { toast.error(e.displayMessage); } finally { setRidBusy(false); }
  };
  // M10A FE2 — Accounts assignment (SOURCE-airport only). Initial: SUPER_ADMIN or assigned Source CTOP.
  // Reassignment (assignee exists): the above PLUS the auto-assigned Source CTRL (never initial).
  const assignedAccounts = findUser(s.accounts_user_id);
  const eligibleAccounts = users.filter(
    (u) => u.role === "ACCOUNTS" && (u.account_status || "ACTIVE") === "ACTIVE" && u.occupancy === "OCCUPIED" && u.airport_code === airport);
  const canInitialAccounts = isSuperAdmin || (profile?.role === "CTOP" && ctopId === profile?.id);
  const canReassignAccounts = canInitialAccounts || (profile?.role === "CTRL" && controllerId === profile?.id);
  const showAccountsControl = assignedAccounts ? canReassignAccounts : canInitialAccounts;
  const assignAccounts = async () => {
    if (!acctPick) return; setAcctBusy(true);
    try { await api.post(`/admin/shipments/${s.id}/city-accounts/assign`, { accounts_profile_id: acctPick }); toast.success(`Accounts ${assignedAccounts ? "reassigned" : "assigned"}`); setAcctPick(""); reload(); }
    catch (e) { toast.error(e.displayMessage); } finally { setAcctBusy(false); }
  };
  return (
    <div className="mb-4 border-t border-[hsl(var(--border))] pt-3" data-testid={`cityops-${side.toLowerCase()}`}>
      <div className="overline mb-1">{side} · {airport}</div>
      <div className="text-sm mb-1" data-testid={`${side.toLowerCase()}-controller-info`}>
        Controller:{" "}
        {controller
          ? <span className="mono">{controller.public_staff_id || controller.email}</span>
          : <span className="text-[hsl(var(--warning))]">Unassigned — admin action required</span>}
      </div>
      {isSuperAdmin && (
        <div className="flex gap-2 mb-2" data-testid={`${side.toLowerCase()}-controller-controls`}>
          <select className="input-field" value={ctrlPick} onChange={(e) => setCtrlPick(e.target.value)} data-testid={`${side.toLowerCase()}-controller-select`}>
            <option value="">{controller ? "Reassign controller…" : `Select ${airport} controller…`}</option>
            {eligibleCtrl.map((u) => <option key={u.id} value={u.id}>{u.public_staff_id || u.email}</option>)}
          </select>
          <button className="btn-secondary" disabled={!ctrlPick || ctrlBusy} onClick={assignController} data-testid={`assign-${side.toLowerCase()}-controller-btn`}>
            {controller ? "Reassign" : "Assign"}
          </button>
        </div>
      )}
      <div className="text-sm mb-2" data-testid={`${side.toLowerCase()}-ctop-info`}>
        CTOP:{" "}
        {assignedCtop
          ? <span className="mono">{assignedCtop.public_staff_id || assignedCtop.email}</span>
          : <span className="text-[hsl(var(--muted))]">Unassigned</span>}
      </div>
      {canAssign && (
        <div className="flex gap-2" data-testid={`${side.toLowerCase()}-ctop-controls`}>
          <select className="input-field" value={pick} onChange={(e) => setPick(e.target.value)} data-testid={`${side.toLowerCase()}-ctop-select`}>
            <option value="">{assignedCtop ? "Reassign to…" : `Select ${airport} CTOP…`}</option>
            {eligible.map((u) => <option key={u.id} value={u.id}>{u.public_staff_id || u.email}</option>)}
          </select>
          <button className="btn-primary" disabled={!pick || busy} onClick={assign} data-testid={`assign-${side.toLowerCase()}-ctop-btn`}>
            {assignedCtop ? "Reassign" : "Assign"}
          </button>
        </div>
      )}
      {canAssign && eligible.length === 0 && (
        <div className="text-xs text-[hsl(var(--warning))] mt-1" data-testid={`${side.toLowerCase()}-no-ctop`}>
          No active CTOP for {airport}.
        </div>
      )}
      <div className="text-sm mt-3 mb-1" data-testid={`${side.toLowerCase()}-captain-info`}>
        {capLabel}:{" "}
        {assignedCap ? <span className="mono">{assignedCap.public_staff_id || assignedCap.email}</span>
          : <span className="text-[hsl(var(--muted))]">Unassigned</span>}
      </div>
      {canAssignCaptain && (
        <div className="flex gap-2 mb-2" data-testid={`${side.toLowerCase()}-captain-controls`}>
          <select className="input-field" value={capPick} onChange={(e) => setCapPick(e.target.value)} data-testid={`${side.toLowerCase()}-captain-select`}>
            <option value="">{assignedCap ? "Reassign captain…" : `Select ${airport} captain…`}</option>
            {eligibleCap.map((u) => <option key={u.id} value={u.id}>{u.public_staff_id || u.email}</option>)}
          </select>
          <button className="btn-primary" disabled={!capPick || capBusy} onClick={assignCaptain} data-testid={`assign-${side.toLowerCase()}-captain-btn`}>
            {assignedCap ? "Reassign" : "Assign"}
          </button>
        </div>
      )}
      {/* Module 7 Unit 4 — Destination Rider removed; rider assignment is SOURCE (pickup) only. */}
      {side === "SOURCE" && (
        <>
          <div className="text-sm mb-1" data-testid={`${side.toLowerCase()}-rider-info`}>
            {ridLabel}:{" "}
            {assignedRid ? <span className="mono">{assignedRid.public_staff_id || assignedRid.email}</span>
              : <span className="text-[hsl(var(--muted))]">Unassigned</span>}
          </div>
          {canAssignOps && (
            <div className="flex gap-2" data-testid={`${side.toLowerCase()}-rider-controls`}>
              <select className="input-field" value={ridPick} onChange={(e) => setRidPick(e.target.value)} data-testid={`${side.toLowerCase()}-rider-select`}>
                <option value="">{assignedRid ? "Reassign rider…" : `Select ${airport} rider…`}</option>
                {eligibleRid.map((u) => <option key={u.id} value={u.id}>{u.public_staff_id || u.email}</option>)}
              </select>
              <button className="btn-primary" disabled={!ridPick || ridBusy} onClick={assignRider} data-testid={`assign-${side.toLowerCase()}-rider-btn`}>
                {assignedRid ? "Reassign" : "Assign"}
              </button>
            </div>
          )}
          <div className="text-sm mt-3 mb-1" data-testid="source-accounts-info">
            Accounts:{" "}
            {assignedAccounts ? <span className="mono">{assignedAccounts.public_staff_id || assignedAccounts.email}</span>
              : <span className="text-[hsl(var(--muted))]">Unassigned</span>}
          </div>
          {showAccountsControl && (
            <div className="flex gap-2" data-testid="source-accounts-controls">
              <select className="input-field" value={acctPick} onChange={(e) => setAcctPick(e.target.value)} data-testid="source-accounts-select">
                <option value="">{assignedAccounts ? "Reassign accounts…" : `Select ${airport} accounts…`}</option>
                {eligibleAccounts.map((u) => <option key={u.id} value={u.id}>{u.public_staff_id || u.email}</option>)}
              </select>
              <button className="btn-primary" disabled={!acctPick || acctBusy} onClick={assignAccounts} data-testid="assign-source-accounts-btn">
                {assignedAccounts ? "Reassign" : "Assign"}
              </button>
            </div>
          )}
          {showAccountsControl && eligibleAccounts.length === 0 && (
            <div className="text-xs text-[hsl(var(--warning))] mt-1" data-testid="source-no-accounts">
              No active Accounts user for {airport}.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function CityOpsPanel({ d, reload, users, profile }) {
  const s = d.shipment;
  const isSuperAdmin = profile?.role === "SUPER_ADMIN";
  const rowProps = { s, isSuperAdmin, profile, users, reload };

  return (
    <Panel icon={<ShieldCheck size={16} weight="bold" />} title="CITY OPERATIONS (SOURCE / DESTINATION)">
      <CityOpsSideRow side="SOURCE" {...rowProps} />
      <CityOpsSideRow side="DESTINATION" {...rowProps} />
      {!isSuperAdmin && (
        <div className="text-xs text-[hsl(var(--muted))]" data-testid="cityops-note">
          Controllers are assigned by SUPER_ADMIN. A City Controller may assign only its own side's CTOP.
        </div>
      )}
    </Panel>
  );
}

export function RiderAssignPanel({ d, reload, users }) {
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [reason, setReason] = useState("");
  const assignedId = d.shipment.pickup_rider_id;
  const assignedRider = users.find((u) => u.id === assignedId);
  const pickupDoneAt = d.shipment.pickup_completed_at;
  const overrideReason = d.shipment.pickup_override_reason;

  // M3.5: legacy rider-assign handler removed from UI; pickup rider is assigned via City Ops.

  const submitOverride = async () => {
    if (!reason || reason.trim().length < 6) {
      toast.error("Override reason must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/admin/shipments/${d.shipment.id}/rider/override`, { reason: reason.trim() });
      toast.success("Override recorded with reason");
      setOverrideOpen(false);
      setReason("");
      reload();
    } catch (e) { toast.error(e.displayMessage); }
  };

  return (
    <Panel icon={<Truck size={16} weight="bold" />} title="PICKUP RIDER STATUS & OVERRIDE">
      {assignedRider ? (
        <div className="text-sm mb-3" data-testid="rider-assigned-info">
          <div className="overline mb-1">ASSIGNED RIDER</div>
          <div className="mono">{assignedRider.email} ({assignedRider.public_staff_id || "—"})</div>
          {d.shipment.pickup_rider_assigned_at && (
            <div className="overline mt-1 text-[hsl(var(--muted))]">
              SINCE {fmtDT(d.shipment.pickup_rider_assigned_at)}
            </div>
          )}
        </div>
      ) : (
        <div className="text-sm text-[hsl(var(--muted))] mb-3" data-testid="rider-unassigned-info">
          No Rider assigned. Pickup-side custody is not anchored.
        </div>
      )}

      {pickupDoneAt ? (
        <div className="text-sm text-[hsl(var(--success))] mb-3" data-testid="rider-pickup-done">
          ✓ Rider pickup complete · {fmtDT(pickupDoneAt)}
          {d.shipment.pickup_confirmations?.closure_type && (
            <div className="overline mt-1">
              CLOSURE: {d.shipment.pickup_confirmations.closure_type}
              {d.shipment.pickup_confirmations.closure_notes
                ? ` · ${d.shipment.pickup_confirmations.closure_notes}`
                : ""}
            </div>
          )}
          {d.shipment.pickup_confirmations?.label_visible && (
            <div className="overline mt-1">AIR12 LABEL VISIBLE · CONFIRMED</div>
          )}
        </div>
      ) : overrideReason ? (
        <div className="text-sm text-[hsl(var(--warning))] mb-3" data-testid="rider-override-active">
          ⚠ Pickup override active · reason: "{overrideReason}"
        </div>
      ) : null}

      {/* M3.5: legacy Rider ASSIGNMENT control hidden — pickup rider is now assigned via City Ops
          (CityOpsSideRow SOURCE rider → POST /city-rider/assign). Admin OVERRIDE below is RETAINED. */}

      {!overrideOpen ? (
        <button
          className="text-xs underline text-[hsl(var(--muted))]"
          onClick={() => setOverrideOpen(true)}
          data-testid="rider-override-open-btn"
        >
          Apply admin override (skip Rider, with reason)
        </button>
      ) : (
        <div className="mt-2 border border-[hsl(var(--border))] rounded p-3" data-testid="rider-override-form">
          <div className="overline mb-1">OVERRIDE REASON (REQUIRED · MIN 6 CHARS)</div>
          <textarea
            className="input-field w-full"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Pilot dry-run — same staff acting as Rider and Source Captain"
            data-testid="rider-override-reason-input"
          />
          <div className="flex gap-2 mt-2">
            <button className="btn-primary" onClick={submitOverride} data-testid="rider-override-submit-btn">
              Record override
            </button>
            <button
              className="text-xs underline"
              onClick={() => { setOverrideOpen(false); setReason(""); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Panel>
  );
}

const _norm = (v) => (v || "").trim().toUpperCase();
const _emptyRow = () => ({ vehicle_number: "", driver_name: "", driver_mobile: "", pickup_eta: "", airport_eta: "", internal_notes: "" });
const _rowFromLeg = (l) => ({
  vehicle_number: l.vehicle_number || "",
  driver_name: l.driver_name || "",
  driver_mobile: l.driver_mobile || "",
  pickup_eta: l.pickup_eta || "",
  airport_eta: l.airport_eta || "",
  internal_notes: l.internal_notes || "",
});

function VehicleSetEditor({ shipmentId, side, committed, disabled, disabledMsg, finaliseBlockedMsg, reload }) {
  const initRows = () => (committed.length ? committed.map(_rowFromLeg) : [_emptyRow()]);
  const [rows, setRows] = useState(initRows);
  const [busy, setBusy] = useState(false);
  const upd = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows([...rows, _emptyRow()]);
  const removeRow = (i) => {
    if (rows.length <= 1) { toast.error("At least one vehicle must remain assigned. Add the replacement vehicle first, then remove this vehicle."); return; }
    setRows(rows.filter((_, idx) => idx !== i));
  };
  const discard = () => setRows(initRows());

  const finalise = async () => {
    for (const r of rows) {
      if ((r.vehicle_number || "").trim().length < 4) { toast.error("Vehicle Number must be at least 4 characters."); return; }
      if (!(r.driver_name || "").trim()) { toast.error("Driver Name is required."); return; }
      if ((r.driver_mobile || "").trim().length < 10) { toast.error("Driver Mobile must be at least 10 digits."); return; }
    }
    const norms = rows.map((r) => _norm(r.vehicle_number));
    if (new Set(norms).size !== norms.length) { toast.error("This vehicle is already added to the shipment."); return; }
    const endpoint = side === "SOURCE" ? "source-vehicles" : "destination-vehicles";
    setBusy(true);
    try {
      const payload = {
        vehicles: rows.map((r) => ({
          vehicle_number: r.vehicle_number.trim(),
          driver_name: r.driver_name.trim(),
          driver_mobile: r.driver_mobile.trim(),
          pickup_eta: r.pickup_eta || null,
          airport_eta: r.airport_eta || null,
          internal_notes: r.internal_notes || null,
        })),
      };
      await api.post(`/admin/shipments/${shipmentId}/${endpoint}/finalise`, payload);
      toast.success(`${side === "SOURCE" ? "Source" : "Destination"} vehicle assignment finalised`);
      reload();
    } catch (e) { toast.error(e.displayMessage || "Could not finalise the vehicle set."); }
    finally { setBusy(false); }
  };

  const label = side === "SOURCE" ? "SOURCE" : "DESTINATION";
  return (
    <Panel icon={<Truck size={16} weight="bold" />} title={`${label} VEHICLE ASSIGNMENT`}>
      {disabled ? (
        <p className="text-sm text-[hsl(var(--muted))]" data-testid={`${side.toLowerCase()}-vehicle-blocked`}>{disabledMsg}</p>
      ) : (
        <div data-testid={`${side.toLowerCase()}-vehicle-editor`} className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="border border-stone-200 rounded-lg p-3" data-testid={`${side.toLowerCase()}-vehicle-card-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="overline">VEHICLE {i + 1}</div>
                <button className="text-xs text-[hsl(var(--danger))]" onClick={() => removeRow(i)} data-testid={`${side.toLowerCase()}-remove-vehicle-${i}`}>Remove</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <input className="input-field" placeholder="Vehicle number" value={r.vehicle_number} onChange={(e) => upd(i, "vehicle_number", e.target.value)} data-testid={`${side.toLowerCase()}-vehicle-number-${i}`} />
                <input className="input-field" placeholder="Driver name" value={r.driver_name} onChange={(e) => upd(i, "driver_name", e.target.value)} data-testid={`${side.toLowerCase()}-driver-name-${i}`} />
                <input className="input-field" placeholder="Driver mobile" value={r.driver_mobile} onChange={(e) => upd(i, "driver_mobile", e.target.value)} data-testid={`${side.toLowerCase()}-driver-mobile-${i}`} />
                <input className="input-field" placeholder="Pickup ETA (ISO, optional)" value={r.pickup_eta} onChange={(e) => upd(i, "pickup_eta", e.target.value)} />
                <input className="input-field" placeholder="Airport ETA (ISO, optional)" value={r.airport_eta} onChange={(e) => upd(i, "airport_eta", e.target.value)} />
                <input className="input-field" placeholder="Internal notes (optional)" value={r.internal_notes} onChange={(e) => upd(i, "internal_notes", e.target.value)} />
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <button className="btn-secondary" onClick={addRow} data-testid={`${side.toLowerCase()}-add-vehicle`}>+ Add Vehicle</button>
            <button className="btn-primary" disabled={busy || !!finaliseBlockedMsg} onClick={finalise} data-testid={`${side.toLowerCase()}-finalise-vehicles`}>Finalise Vehicle Assignment</button>
            <button className="btn-secondary" disabled={busy} onClick={discard} data-testid={`${side.toLowerCase()}-discard-vehicles`}>Cancel / Discard Changes</button>
          </div>
          {finaliseBlockedMsg && (
            <p className="text-xs text-[hsl(var(--danger))]" data-testid={`${side.toLowerCase()}-finalise-blocked-hint`}>{finaliseBlockedMsg}</p>
          )}
        </div>
      )}
    </Panel>
  );
}

export function OriginVehiclePanel({ d, reload }) {
  // B1 (Founder-locked) — an active Shipment Block withholds Finalise only; backend stays authoritative.
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    api.get(`/shipments/${d.shipment.id}/blocks`).then((r) => setBlocked(!!r.data?.active)).catch(() => setBlocked(false));
  }, [d.shipment.id, d.shipment.current_status]);
  const committed = (d.road_legs || []).filter((l) => l.leg_type === "SRC");
  const disabled = !["SENDER_READY_CONFIRMED", "SOURCE_VEHICLE_ASSIGNED"].includes(d.shipment.current_status)
    || !!d.shipment.pickup_completed_at;
  return (
    <VehicleSetEditor
      shipmentId={d.shipment.id} side="SOURCE" committed={committed}
      disabled={disabled}
      disabledMsg="Source vehicles can be edited after the sender confirms readiness and before pickup is completed."
      finaliseBlockedMsg={blocked ? "Shipment blocked. Resolve the Block before finalising vehicles." : null}
      reload={reload}
    />
  );
}

export function DestinationVehiclePanel({ d, reload }) {
  const committed = (d.road_legs || []).filter((l) => l.leg_type === "DST");
  const disabled = !["RECEIVER_READY_CONFIRMED", "DESTINATION_VEHICLE_ASSIGNED"].includes(d.shipment.current_status);
  return (
    <VehicleSetEditor
      shipmentId={d.shipment.id} side="DESTINATION" committed={committed}
      disabled={disabled}
      disabledMsg="Destination vehicles can be edited after receiver readiness and before out-for-delivery."
      reload={reload}
    />
  );
}
